/**
 * Email Template Manager Component
 * Allows users to create, edit, view, and delete custom email templates for OA reporting
 */

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Wand2, Loader2, ChevronUp, ChevronDown, Copy, Info, ChevronRight } from 'lucide-react';
import { userApi, documentApi, patentApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { OA_EMAIL_TEMPLATES } from '@/config/emailTemplates';

interface EmailTemplate {
  prompt_id: string;
  name: string;
  description: string;
  template_structure: {
    greeting: string;
    intro: string;
    sections: string[];
    closing: string;
    signature: string;
    tone: string;
    use_standard_sections?: boolean;
  };
  created_at: string;
}

const STANDARD_SECTIONS = [
  'KEY INFORMATION',
  'CLAIMS STATUS',
  'REJECTION DETAILS',
  'PRIOR ART CITED',
  'FORMAL OBJECTIONS',
  'IMPORTANT DEADLINES',
];

const SECTION_HINTS: Record<string, string> = {
  'KEY INFORMATION': 'Application number, action type, mailing date, response deadline, examiner details',
  'CLAIMS STATUS': 'Total claims, rejected/allowed/allowable claims with claim numbers',
  'REJECTION DETAILS': 'Specific rejection types (35 USC 102, 103, 112), examiner reasoning',
  'PRIOR ART CITED': 'List of cited prior art references and key teachings',
  'FORMAL OBJECTIONS': 'Objections to specification, drawings, or claim dependencies',
  'IMPORTANT DEADLINES': 'Response deadline, shortened statutory period, issue fee deadlines',
  'SUMMARY': 'High-level overview of the office action and key issues',
  'KEY DEADLINES': 'All critical dates and deadlines requiring action',
  'IMMEDIATE ACTION REQUIRED': 'Urgent items needing immediate attention',
  'NEXT STEPS': 'Recommended actions and follow-up items',
  'EXECUTIVE SUMMARY': 'Concise overview of the office action highlights',
  'OFFICE ACTION DETAILS': 'Full details of the office action document',
  'STRATEGIC RECOMMENDATIONS': 'Attorney recommendations for responding to the action',
  'CLAIMS STATUS ANALYSIS': 'Detailed breakdown of claim rejections, allowances, and objections',
  'DETAILED REJECTION ANALYSIS': 'In-depth analysis of each rejection ground',
  'PRIOR ART ASSESSMENT': 'Evaluation of cited references and their relevance',
  'TECHNICAL COMPARISON': 'Comparison of invention against cited prior art',
  'PROPOSED AMENDMENTS': 'Suggested claim amendments to overcome rejections',
  'ARGUMENT STRATEGY': 'Strategy for arguments to traverse rejections',
  'TIMELINE AND DEADLINES': 'All relevant dates and filing deadlines',
  'COST ESTIMATE': 'Estimated cost to respond to the office action',
};

const TONE_PRESETS = ['Professional', 'Concise', 'Client-Friendly', 'Technical', 'Congratulatory'];

const AVAILABLE_PLACEHOLDERS = [
  '[Client Name]',
  '[Action Type]',
  '[Mailing Date]',
  '[Application Number]',
  '[Application Title]',
  '[Response Deadline]',
  '[Attorney/Agent Name]',
  '[Firm Name]',
  '[Contact Information]',
  '[Registration Number]',
  '[Phone]',
  '[Email]',
];

const DEFAULT_TEMPLATE_TONES: Record<string, string> = {
  standard: 'Professional',
  concise: 'Concise',
  detailed: 'Technical',
  client_friendly: 'Client-Friendly',
  allowance: 'Congratulatory',
};

export const EmailTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [activeTab, setActiveTab] = useState('manual');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    greeting: 'Dear [Client Name],',
    intro: '',
    sections: [] as string[],
    closing: '',
    signature: 'Best regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]',
    tone: 'Professional',
  });

  // Section builder state
  const [newSectionInput, setNewSectionInput] = useState('');

  // Placeholder reference panel
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  // Auto-generate state
  const [rawEmailText, setRawEmailText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Notification modal state
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const showNotification = (title: string, message: string, type: 'success' | 'error' = 'success') => {
    setNotificationModal({ isOpen: true, title, message, type });
  };

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await userApi.fetchEmailTemplates();
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Section builder helpers ---
  const addSection = (name: string) => {
    const trimmed = name.trim().toUpperCase();
    if (!trimmed || formData.sections.includes(trimmed)) return;
    setFormData(fd => ({ ...fd, sections: [...fd.sections, trimmed] }));
  };

  const removeSection = (idx: number) => {
    setFormData(fd => ({ ...fd, sections: fd.sections.filter((_, i) => i !== idx) }));
  };

  const moveSection = (idx: number, dir: 'up' | 'down') => {
    setFormData(fd => {
      const sections = [...fd.sections];
      const target = dir === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= sections.length) return fd;
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...fd, sections };
    });
  };

  // --- Copy from default template ---
  const copyFromDefault = (templateId: string) => {
    const tpl = OA_EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    setFormData(fd => ({
      ...fd,
      greeting: tpl.structure.greeting,
      intro: tpl.structure.intro,
      sections: [...tpl.structure.sections],
      closing: tpl.structure.closing,
      signature: tpl.structure.signature,
      tone: DEFAULT_TEMPLATE_TONES[templateId] || 'Professional',
    }));
  };

  // --- Save ---
  const handleSave = async () => {
    try {
      await userApi.saveEmailTemplate(
        formData.name,
        formData.description,
        {
          greeting: formData.greeting,
          intro: formData.intro,
          sections: formData.sections,
          closing: formData.closing,
          signature: formData.signature,
          tone: formData.tone,
        },
        editingTemplate?.prompt_id
      );
      await fetchTemplates();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save email template:', error);
      showNotification('Error', 'Failed to save template. Please try again.', 'error');
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await userApi.deleteEmailTemplate(templateId);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete email template:', error);
      showNotification('Error', 'Failed to delete template. Please try again.', 'error');
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      greeting: template.template_structure.greeting,
      intro: template.template_structure.intro,
      sections: [...template.template_structure.sections],
      closing: template.template_structure.closing,
      signature: template.template_structure.signature,
      tone: template.template_structure.tone || 'Professional',
    });
    setNewSectionInput('');
    setShowPlaceholders(false);
    setActiveTab('manual');
    setIsDialogOpen(true);
  };

  const handleDuplicate = (template: EmailTemplate) => {
    setEditingTemplate(null);
    setFormData({
      name: `Copy of ${template.name}`,
      description: template.description,
      greeting: template.template_structure.greeting,
      intro: template.template_structure.intro,
      sections: [...template.template_structure.sections],
      closing: template.template_structure.closing,
      signature: template.template_structure.signature,
      tone: template.template_structure.tone || 'Professional',
    });
    setNewSectionInput('');
    setShowPlaceholders(false);
    setActiveTab('manual');
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      greeting: 'Dear [Client Name],',
      intro: '',
      sections: [],
      closing: '',
      signature: 'Best regards,\n\n[Attorney/Agent Name]\n[Firm Name]\n[Contact Information]',
      tone: 'Professional',
    });
    setNewSectionInput('');
    setShowPlaceholders(false);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
    setRawEmailText('');
    setActiveTab('manual');
  };

  const handleAutoGenerate = async () => {
    if (!rawEmailText.trim()) {
      showNotification('Input Required', 'Please paste an email to analyze', 'error');
      return;
    }
    setIsGenerating(true);
    try {
      const response = await userApi.autoGenerateEmailTemplate(rawEmailText);
      if (!response || !response.job_id) {
        showNotification('Error', 'Failed to start template generation. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }
      const jobId = response.job_id;
      const maxAttempts = 60;
      let attempts = 0;
      let status = 'Processing';
      while (attempts < maxAttempts && status !== 'Complete' && status !== 'Failed') {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const statusResponse = await documentApi.getJobStatus(jobId);
        status = statusResponse?.status || (statusResponse as string);
        attempts++;
      }
      if (status !== 'Complete') {
        showNotification(
          'Generation Failed',
          `Template generation ${status === 'Failed' ? 'failed' : 'timed out'}. Please try again.`,
          'error'
        );
        setIsGenerating(false);
        return;
      }
      const result = await patentApi.getCustomAnalysisResult(jobId);
      let templateData;
      try {
        if (typeof result.analysis === 'string') {
          try {
            templateData = JSON.parse(result.analysis);
          } catch {
            const jsonString = result.analysis
              .replace(/'/g, '"')
              .replace(/None/g, 'null')
              .replace(/True/g, 'true')
              .replace(/False/g, 'false');
            templateData = JSON.parse(jsonString);
          }
        } else {
          templateData = result.analysis;
        }
      } catch (e) {
        console.error('Failed to parse template data:', e, 'Raw data:', result.analysis);
        showNotification('Parse Error', 'Failed to parse generated template. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }
      if (!templateData || !templateData.template_structure) {
        console.error('Invalid template data structure:', templateData);
        showNotification('Invalid Data', 'Received invalid template data. Please try again.', 'error');
        setIsGenerating(false);
        return;
      }
      const templateStruct = templateData.template_structure;
      setFormData({
        name: templateData.name || 'Auto-generated Template',
        description: templateData.description || '',
        greeting: templateStruct.greeting || 'Dear [Client Name],',
        intro: templateStruct.intro || '',
        sections: templateStruct.sections || [],
        closing: templateStruct.closing || '',
        signature: templateStruct.signature || '',
        tone: templateStruct.tone || 'Professional',
      });
      setActiveTab('manual');
      showNotification('Success', 'Template generated! Review the fields in the Manual tab and save when ready.', 'success');
    } catch (error) {
      console.error('Failed to auto-generate template:', error);
      showNotification('Error', 'Failed to generate template. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  // Standard sections not yet added — for quick-add palette
  const suggestedSections = STANDARD_SECTIONS.filter(s => !formData.sections.includes(s));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Email Templates</h2>
          <p className="text-gray-600 mt-1">
            Create and manage custom templates for Office Action reporting emails
          </p>
        </div>
        <Dialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            if (open) setIsDialogOpen(true);
            else handleDialogClose();
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Email Template' : 'Create New Email Template'}
              </DialogTitle>
              <DialogDescription>
                {editingTemplate
                  ? 'Update your custom email template'
                  : 'Create manually or auto-generate from a past email'}
              </DialogDescription>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual">Manual</TabsTrigger>
                <TabsTrigger value="auto" disabled={!!editingTemplate}>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Generate
                </TabsTrigger>
              </TabsList>

              {/* ── Auto-Generate tab ── */}
              <TabsContent value="auto" className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="rawEmail">Paste Past Email</Label>
                  <Textarea
                    id="rawEmail"
                    value={rawEmailText}
                    onChange={(e) => setRawEmailText(e.target.value)}
                    placeholder="Paste a complete email message here. The AI will analyze it and extract a reusable template structure..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Paste a complete Office Action reporting email. The AI will identify patterns,
                    extract placeholders, and create a template.
                  </p>
                </div>
                <Button
                  onClick={handleAutoGenerate}
                  disabled={isGenerating || !rawEmailText.trim()}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Email...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate Template from Email
                    </>
                  )}
                </Button>
                {formData.name && !isGenerating && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      Template generated! Switch to the "Manual" tab to review and save.
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* ── Manual tab ── */}
              <TabsContent value="manual" className="space-y-4 py-4">

                {/* Copy from default — only for new templates */}
                {!editingTemplate && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Copy from default template
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {OA_EMAIL_TEMPLATES.map(tpl => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => copyFromDefault(tpl.id)}
                          className="text-xs px-2.5 py-1 rounded-full border border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition-colors"
                        >
                          {tpl.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Name */}
                <div className="space-y-1">
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Custom Template"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of when to use this template"
                  />
                </div>

                {/* Placeholder reference (collapsible) */}
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowPlaceholders(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <span className="flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5" />
                      Available placeholders — click any to copy
                    </span>
                    <ChevronRight
                      className={`w-3.5 h-3.5 transition-transform ${showPlaceholders ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {showPlaceholders && (
                    <div className="px-3 pb-3 pt-2 flex flex-wrap gap-1.5 bg-white">
                      {AVAILABLE_PLACEHOLDERS.map(ph => (
                        <button
                          key={ph}
                          type="button"
                          title="Click to copy"
                          onClick={() => navigator.clipboard.writeText(ph)}
                          className="text-xs font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 active:bg-blue-200 transition-colors"
                        >
                          {ph}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Greeting */}
                <div className="space-y-1">
                  <Label htmlFor="greeting">Greeting</Label>
                  <Input
                    id="greeting"
                    value={formData.greeting}
                    onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                    placeholder="Dear [Client Name],"
                  />
                </div>

                {/* Intro */}
                <div className="space-y-1">
                  <Label htmlFor="intro">Introduction Paragraph *</Label>
                  <Textarea
                    id="intro"
                    value={formData.intro}
                    onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                    placeholder="We received a [Action Type] dated [Mailing Date]..."
                    rows={3}
                  />
                </div>

                {/* ── Section builder ── */}
                <div className="space-y-2">
                  <Label>Email Sections *</Label>

                  {/* Current sections — ordered list */}
                  {formData.sections.length > 0 ? (
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                      {formData.sections.map((section, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50"
                        >
                          <span className="text-xs text-slate-400 w-5 text-right select-none shrink-0">
                            {idx + 1}.
                          </span>
                          <span className="flex-1 text-sm font-medium text-slate-700 truncate">
                            {section}
                          </span>
                          {SECTION_HINTS[section] && (
                            <span
                              className="text-xs text-slate-400 italic hidden md:block max-w-[180px] truncate shrink-0"
                              title={SECTION_HINTS[section]}
                            >
                              {SECTION_HINTS[section]}
                            </span>
                          )}
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button
                              type="button"
                              onClick={() => moveSection(idx, 'up')}
                              disabled={idx === 0}
                              className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                            >
                              <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSection(idx, 'down')}
                              disabled={idx === formData.sections.length - 1}
                              className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                            >
                              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeSection(idx)}
                              className="p-1 rounded hover:bg-red-100"
                              title="Remove section"
                            >
                              <X className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 py-5 text-center text-sm text-slate-400">
                      No sections yet — use quick-add or type a custom name below.
                    </div>
                  )}

                  {/* Quick-add palette */}
                  {suggestedSections.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-slate-400">Quick-add:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {suggestedSections.map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => addSection(s)}
                            title={SECTION_HINTS[s]}
                            className="text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-teal-50 hover:border-teal-400 hover:text-teal-700 transition-colors"
                          >
                            + {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom section input */}
                  <div className="flex gap-2">
                    <Input
                      value={newSectionInput}
                      onChange={(e) => setNewSectionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addSection(newSectionInput);
                          setNewSectionInput('');
                        }
                      }}
                      placeholder="Type a custom section name and press Enter…"
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        addSection(newSectionInput);
                        setNewSectionInput('');
                      }}
                      disabled={!newSectionInput.trim()}
                      className="shrink-0"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Closing */}
                <div className="space-y-1">
                  <Label htmlFor="closing">Closing Paragraph *</Label>
                  <Textarea
                    id="closing"
                    value={formData.closing}
                    onChange={(e) => setFormData({ ...formData, closing: e.target.value })}
                    placeholder="Please let us know if you would like to schedule a call..."
                    rows={3}
                  />
                </div>

                {/* Signature */}
                <div className="space-y-1">
                  <Label htmlFor="signature">Signature</Label>
                  <Textarea
                    id="signature"
                    value={formData.signature}
                    onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                    rows={4}
                  />
                </div>

                {/* Tone presets */}
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <div className="flex flex-wrap gap-2">
                    {TONE_PRESETS.map(preset => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setFormData(fd => ({ ...fd, tone: preset }))}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          formData.tone === preset
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <Input
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                    placeholder="Or type a custom tone (e.g. 'Direct and brief')"
                    className="text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Choose a preset or describe a custom tone to guide the AI's writing style.
                  </p>
                </div>

              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  !formData.name ||
                  !formData.intro ||
                  formData.sections.length === 0 ||
                  !formData.closing
                }
              >
                <Save className="w-4 h-4 mr-2" />
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Template list ── */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No custom templates yet</p>
            <p className="text-sm text-gray-400 mt-2">
              Create your first template to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2">
          {templates.map((template) => (
            <Card key={template.prompt_id} className="w-full">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.description && (
                      <CardDescription className="mt-1">{template.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(template)}
                      title="Duplicate template"
                    >
                      <Copy className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(template)}
                      title="Edit template"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.prompt_id)}
                      title="Delete template"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {/* Section chips */}
                <div className="flex flex-wrap gap-1.5">
                  {template.template_structure.sections.slice(0, 5).map((section, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
                    >
                      {section}
                    </span>
                  ))}
                  {template.template_structure.sections.length > 5 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                      +{template.template_structure.sections.length - 5} more
                    </span>
                  )}
                </div>
                {/* Tone + date */}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>
                    <span className="font-medium text-gray-600">Tone:</span>{' '}
                    {template.template_structure.tone}
                  </span>
                  <span className="ml-auto">
                    {new Date(template.created_at).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Notification Modal */}
      <Dialog
        open={notificationModal.isOpen}
        onOpenChange={(open) => setNotificationModal({ ...notificationModal, isOpen: open })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle
              className={notificationModal.type === 'error' ? 'text-red-600' : 'text-green-600'}
            >
              {notificationModal.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700">{notificationModal.message}</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setNotificationModal({ ...notificationModal, isOpen: false })}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
